import { closeCompletion, startCompletion } from '@codemirror/autocomplete';
import {
  foldedRanges,
  forceParsing,
  indentUnit,
  syntaxTreeAvailable,
} from '@codemirror/language';
import {
  Annotation,
  Compartment,
  EditorSelection,
  EditorState,
  Extension,
  Prec,
  Transaction,
} from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import clsx from 'clsx';
import {
  forwardRef,
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import * as styles from './BaseCodeEditor.sty.js';
import type { EditorLanguageConfiguration } from './configuration/configuration.js';
import { configureFormatKeymap } from './configuration/configure-format-keymap.js';
import { configureLineWrap } from './configuration/configure-line-wrap.js';
import { configurePlaceholder } from './configuration/configure-placeholder.js';
import { configureReadOnly } from './configuration/configure-read-only.js';
import { configureSpellCheck } from './configuration/configure-spell-check.js';
import {
  getMultipleLinesDefaultConfiguration,
  getSingleLineDefaultConfiguration,
  getDefaultConfiguration,
} from './configuration/getDefaultConfigurations.js';
import type { GutterConfiguration } from './configuration/getDefaultConfigurations.js';
import { getLanguageConfiguration } from './configuration/language-extensions.js';
import { highlightExtension } from './highlight/highlight.extension.js';
import {
  dataTestId as dataTestIdAttr,
  baseCodeEditorTestId,
  baseCodeEditorRootTestId,
  baseCodeEditorScrollerTestId,
} from './testIds.js';
import { calculateFolding } from './utils/calculate-folding.js';
import { getKeyMaps } from './utils/key-maps.js';
import { IntlProps } from './utils/render-node.js';
import { runFoldings } from './utils/run-foldings.js';
import { sanitizeText } from './utils/sanitize-text.js';
import { sanitizeValue } from './utils/sanitize-value.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { devLog } from '../../core/utils/logging.js';
import { InternalBaseItem as BaseItem } from '../../forms/forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useSyncedFormControlProps } from '../../forms/forms-core/hooks/useSyncedFormControlProps.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';

// We disable the unstable EDIT_CONTEXT feature to ensure codemirror doesn't capture input after losing focus.
// According to the maintainer it should be fixed with an upcoming chrome release, but we don't know about about Safari.
// Github Issue https://github.com/codemirror/dev/issues/1537
// @ts-expect-error - the prop exists but seems not to be included in the codemirror types.
EditorView.EDIT_CONTEXT = false;

// #region typings
/**
 * Props shared by all code editor components.
 * @public
 */
export interface SharedBaseCodeEditorProps
  extends FormControlStateProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The ID for the DOM element.
   */
  id?: string;
  /**
   * Callback that is called when the value in the editor changes.
   */
  onChange?: (value: string) => void;
  /**
   * Displayed initially in the editor when there is no other content.
   */
  placeholder?: string;
  /**
   * Whether spellcheck should be enabled.
   * @defaultValue false
   */
  spellCheck?: boolean;
  /**
   * If set to true, the code editor uses the full height available in its parent.
   * @defaultValue false
   */
  fullHeight?: boolean;
  /**
   * The start indices (character position) that should be folded initially in uncontrolled scenarios.
   */
  defaultFolding?: number[];
  /**
   * The start indices (character position) that should be folded initially in controlled scenarios.
   */
  folding?: number[];
  /**
   * Callback that is called when folding changes.
   */
  onFoldingChange?: (values: number[]) => void;
  /**
   * Whether the input is readonly.
   * @defaultValue false
   */
  readOnly?: boolean;
  /**
   * Whether long lines should be wrapped.
   * @defaultValue false
   */
  lineWrap?: boolean;
  /**
   * Callback that is called when the editor loses focus.
   */
  onBlur?: (e: FocusEvent) => void;
  /**
   * Callback that is called when the editor receives focus.
   */
  onFocus?: (e: FocusEvent) => void;
  /**
   * Editor layout size, 'default' for standard spacing and 'condensed' for reduced font size, padding and margins.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';
  /**
   * If set to true, `aria-required` will be set to true on the input element.
   * @defaultValue false
   */
  required?: boolean;
  /**
   * Opts in to fully controlled semantics on `value`. When `true`, every user
   * keystroke is blocked until the consumer updates `value` in response to
   * `onChange`; the editor always reflects `value` exactly.
   *
   * When `false` (the default), `value` seeds and re-syncs the document but
   * user keystrokes are applied immediately without waiting for `onChange`.
   *
   * @remarks
   * IME composition transactions are always let through so that candidate
   * characters display correctly during an active IME session.
   * Autocomplete completions (selecting an item) are applied immediately and
   * forwarded via `onChange` so the consumer can accept the new value.
   *
   * @deprecated Will be removed when controlled behaviour becomes the default.
   * @defaultValue false
   */
  isControlled?: boolean;
}

/**
 * Base props shared by both controlled and uncontrolled `BaseCodeEditor` usages.
 * @internal
 **/
export interface BaseCodeEditorBaseProps extends SharedBaseCodeEditorProps {
  /**
   * The language configuration.
   */
  configuration?: EditorLanguageConfiguration;
  /**
   * If set to true, the code editor allows only one line and the line number will not be displayed.
   * @defaultValue false
   */
  singleLine?: boolean;
  /**
   * If set to true, the code editor will hide the line numbers.
   * @defaultValue false
   */
  hideLineNumbers?: boolean;
  /**
   * Provides the gutter configuration to display a marker next to the fold gutter.
   */
  gutterConfiguration?: GutterConfiguration;
  /**
   * Formatter function that receives the current value and returns the formatted value.
   * When provided, registers a keyboard shortcut for formatting.
   */
  format?: (value: string) => string;
}

/**
 * Props for the `BaseCodeEditor` in controlled usage.
 * @internal
 */
export type BaseCodeEditorControlledProps = {
  /**
   * The contents of the editor. Treated as the initial CodeMirror document and
   * synchronised with the editor on subsequent prop changes.
   * @defaultValue ''
   */
  value?: string;
  defaultValue?: never;
};

/**
 * Props for the `BaseCodeEditor` in uncontrolled usage.
 * @internal
 */
export type BaseCodeEditorUncontrolledProps = {
  value?: never;
  /**
   * The initial contents of the editor for uncontrolled usage. Used only when the
   * editor mounts; subsequent changes to this prop are ignored.
   */
  defaultValue?: string;
};

/**
 * Props used for BaseCodeEditor.
 * @internal
 */
export type BaseCodeEditorProps = BaseCodeEditorBaseProps &
  (BaseCodeEditorControlledProps | BaseCodeEditorUncontrolledProps);

/**
 * public ref type
 * @internal
 */
export type BaseCodeEditorRef = {
  focus: () => void;
  selectRange: (range: { from: number; to: number }) => void;
  readonly element: HTMLDivElement | null;
  setCursorPosition: (position: number | 'end') => void;
  editorView?: () => EditorView;
  format?: () => void;
};
// #endregion typings

// Annotation that marks a transaction dispatched by the controlled-mode prop sync,
// allowing the transactionFilter to let it through without blocking or calling onChange.
const controlledSyncAnnotation = Annotation.define<true>();

const languageCompartment = new Compartment();
const editorBaseConfigCompartment = new Compartment();
const lineWrappingCompartment = new Compartment();
const keymapCompartment = new Compartment();
const placeholderCompartment = new Compartment();
const highlightOnChangeCompartment = new Compartment();
const formatKeymapCompartment = new Compartment();

const getEditorConfiguration = (
  intlProps: IntlProps,
  options: {
    singleLine: boolean;
    hideLineNumbers?: boolean;
    gutterConfiguration?: GutterConfiguration;
    required?: boolean;
    size?: 'default' | 'condensed';
  },
): Extension[] => {
  const { singleLine, gutterConfiguration, hideLineNumbers, required, size } =
    options;

  const isRequired = required ?? false;
  const editorSize = size ?? 'default';

  if (singleLine) {
    return getSingleLineDefaultConfiguration(intlProps, {
      required: isRequired,
      size: editorSize,
    });
  }
  if (hideLineNumbers) {
    return getDefaultConfiguration(intlProps, {
      required: isRequired,
      size: editorSize,
    });
  }
  return getMultipleLinesDefaultConfiguration(intlProps, {
    gutterConfiguration,
    required: isRequired,
    size: editorSize,
  });
};

function useLazyEditorViewRef(): MutableRefObject<EditorView> {
  const viewRef = useRef<EditorView | null>(null);

  if (viewRef.current === null) {
    const view = new EditorView({
      state: EditorState.create(),
    });

    // Harden `posAtCoords` against CodeMirror's coordinate scan throwing when
    // the editor content has no client rects. This happens when a hover
    // handler (e.g. the lint diagnostics tooltip, always installed on the DQL
    // editor) fires while the editor — or an ancestor — is hidden, detached,
    // or mid-layout, so `getClientRects()` returns empty for every tile. The
    // scan then dereferences an undefined rect and throws "Cannot read
    // properties of undefined (reading 'top')", which escapes the delayed
    // hover handler as an unhandled error. Returning `null` makes the hover
    // tooltip plugin bail gracefully, exactly as it does for an off-content
    // coordinate.
    const originalPosAtCoords = view.posAtCoords.bind(view);
    view.posAtCoords = ((coords, precise) => {
      try {
        return originalPosAtCoords(coords, precise);
      } catch {
        return null;
      }
    }) as EditorView['posAtCoords'];

    // Similar override for the posAndSideAtCoords method, fixes the exact same issue
    // when `getClientRects()` returns empty during a coordinate scan.
    // Happens when a DOM mouse event (e.g. mousedown for text selection via
    // basicMouseSelection) fires while the editor — or an ancestor — is
    // hidden, detached, or mid-layout, so `getClientRects()` returns empty
    // for every tile.
    // Returning a safe fallback position (document start) avoids the crash.
    const originalPosAndSideAtCoords = view.posAndSideAtCoords.bind(view);
    view.posAndSideAtCoords = ((coords, precise) => {
      try {
        return originalPosAndSideAtCoords(coords, precise);
      } catch {
        return { pos: 0, assoc: 1 };
      }
    }) as EditorView['posAndSideAtCoords'];

    viewRef.current = view;
  }

  // it can never be null, the cast fixes the type
  return viewRef as MutableRefObject<EditorView>;
}

/**
 * Creates a CodeMirror extension that attaches DOM event handlers to the editor's content element.
 *
 * @returns An extension that intercepts `keydown`, `focus`, and `blur` events.
 */
const getDomEventHandlers = (
  onFocus?: (e: FocusEvent) => void,
  onBlur?: (e: FocusEvent) => void,
) => {
  return EditorView.domEventHandlers({
    // Prevent the Escape key from propagating beyond the editor.
    // This is necessary for editors that are placed inside an overlay (e.g. Modal),
    // where pressing `Escape` to exit the edit mode would otherwise close the overlay.
    keydown: (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
      }
    },
    // when the editor content element receives focus, call the provided focus handler
    focus: (event: FocusEvent) => {
      onFocus?.(event);
    },
    // when the editor content element loses focus, call the provided blur handler
    blur: (event: FocusEvent) => {
      onBlur?.(event);
    },
  });
};

/**
 * Provides a base/core component for other code editors to share a common base.
 *
 * @internal
 */
export const BaseCodeEditor = /* @__PURE__ */ forwardRef<
  BaseCodeEditorRef,
  BaseCodeEditorProps
>((props, forwardedRef) => {
  const {
    controlState,
    value,
    defaultValue,
    readOnly = false,
    spellCheck = false,
    lineWrap = false,
    onChange,
    onFoldingChange,
    defaultFolding,
    folding,
    fullHeight = false,
    placeholder,
    configuration,
    singleLine = false,
    hideLineNumbers = false,
    size = 'default',
    onFocus,
    onBlur,
    gutterConfiguration,
    id: propId,
    required = false,
    format,
    // DEPRECATION: [APPDEV-17404] BREAKING-CHANGE: Remove isControlled prop.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    isControlled = false,
    style: consumerStyle,
    className: consumerClassName,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-details': ariaDetails,
    'aria-describedby': ariaDescribedBy,
    'data-testid': dataTestId = baseCodeEditorRootTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': dataDtComponent,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const intl = useIntl();
  const [hasError, hint] = useFormControlState(controlState);
  const containerRef = useRef<HTMLDivElement>(null);

  const rootRef = useMergeRefs<HTMLDivElement>([containerRef]);

  /**
   * Sanitize value
   */
  const sanitizedOnChange = useCallback(
    (value: string) => onChange?.(sanitizeText(value)),
    [onChange],
  );

  const viewRef = useLazyEditorViewRef();

  // Held in a ref so the imperative `format()` handle — captured once by
  // useImperativeHandle — always sees the latest formatter. `formatFunction` can
  // arrive asynchronously (e.g. DQL's dynamic dqlint import), so the first-render
  // value may be undefined.
  const formatFunctionRef = useRef(format);
  useEffect(() => {
    formatFunctionRef.current = format;
  }, [format]);

  const handleFormat = useCallback(() => {
    const currentFormatFunction = formatFunctionRef.current;
    if (!currentFormatFunction) {
      devLog(
        'BaseCodeEditor: format() called but no format function is provided.',
      );
      return;
    }
    const editorView = viewRef.current;
    const currentValue = editorView.state.doc.toString();
    let formattedValue: string;
    try {
      formattedValue = currentFormatFunction(currentValue);
    } catch (error) {
      devLog(
        'BaseCodeEditor: format function threw an error, leaving content unchanged.',
        error,
      );
      return;
    }
    if (!formattedValue || formattedValue === currentValue) {
      return;
    }
    editorView.dispatch({
      changes: {
        from: 0,
        to: editorView.state.doc.length,
        insert: formattedValue,
      },
    });
  }, [viewRef]);

  const { id } = useSyncedFormControlProps({
    propId,
  });

  const valueChangeRef = useRef<(value: string) => void>(undefined);
  // Reference of the currently applied value, stored in a ref to be accessed correctly inside the codeMirror listeners.
  const valueRef = useRef(value ?? defaultValue ?? '');
  // Flag indicating that the document has been changed by someone other than the user. Mainly through setting the value prop.
  const isMarkedDirtyRef = useRef<boolean>(false);

  const prevEditorConfigRef = useRef<
    | {
        singleLine: boolean;
        hideLineNumbers: boolean;
        gutterConfiguration: GutterConfiguration | undefined;
        required: boolean;
        size: 'default' | 'condensed';
        locale: string;
        defaultLocale: string;
        messages: IntlProps['messages'];
      }
    | undefined
  >(undefined);

  const prevLangConfigRef = useRef<
    | {
        language: EditorLanguageConfiguration['language'];
        extendWith: EditorLanguageConfiguration['extendWith'];
        autocompleteOptions: EditorLanguageConfiguration['autocompleteOptions'];
        locale: string;
        defaultLocale: string;
        messages: IntlProps['messages'];
      }
    | undefined
  >(undefined);

  const [previousValue, setPreviousValue] = useState<string>('');
  // Reference of the transaction where the doc (CodeMirror value) was last changed. Used mainly to get most up-to-date cursor position.
  const transactionRef = useRef<Transaction | undefined>(undefined);
  // Stable ref for isControlled so the transactionFilter closure always sees the current value.
  const isControlledRef = useRef(isControlled);
  // Tracks IME composition state: true while a composition session is active, so the
  // transactionFilter does not block intermediate composition transactions.
  const isUserEventComposeRef = useRef(false);
  // Tracks the last value synced to the editor in controlled mode to detect prop changes
  // during render without an extra useEffect round-trip.
  const prevControlledValueRef = useRef<string | undefined>(undefined);
  // True when the controlled sync microtask should call startCompletion after updating the doc.
  // Set by the transactionFilter when it blocks a user typing transaction; cleared by the microtask.
  const pendingStartCompletionRef = useRef(false);

  const { focusProps, isFocusVisible } = useFocusRing({
    color: hasError ? 'critical' : 'neutral',
  });

  const [controlledFolding] = useControllableState<number[] | undefined>({
    defaultValue: defaultFolding,
    onChange: (values?: number[]) => {
      if (values) {
        onFoldingChange?.(values);
      }
    },
    value: folding,
  });

  useImperativeHandle(
    forwardedRef,
    () => ({
      element: containerRef.current,
      editorView: () => viewRef.current,
      focus: () => viewRef.current.focus(),
      selectRange: (range) => {
        const editorView = viewRef.current;
        if (editorView && range) {
          const docLength = editorView.state.doc.length;
          const from = range.from < docLength ? range.from : 0;
          const to = range.to < docLength ? range.to : docLength;

          const transaction = editorView.state.update({
            selection: EditorSelection.range(to, from),
            scrollIntoView: true,
          });
          editorView.dispatch(transaction);
          editorView.focus();
        }
      },
      setCursorPosition: (position: number | 'end') => {
        viewRef.current.dispatch({
          selection: EditorSelection.cursor(
            typeof position === 'number'
              ? Math.min(
                  Math.max(position, 0),
                  viewRef.current.state.doc.length,
                )
              : viewRef.current.state.doc.length,
          ),
          scrollIntoView: true,
        });
        viewRef.current.focus();
      },
      format: handleFormat,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Create editor view
  useEffect(() => {
    const editorView = viewRef.current;
    containerRef.current?.appendChild(editorView.dom);
    editorView.dom.setAttribute(dataTestIdAttr, baseCodeEditorTestId);

    return () => editorView.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sets aria labels
  useEffect(() => {
    const { contentDOM } = viewRef.current;
    const ariaLabelAttribute = 'aria-label';
    const ariaLabelledByAttribute = 'aria-labelledby';
    const ariaDetailsAttribute = 'aria-details';
    const ariaDescribedByAttribute = 'aria-describedby';

    if (ariaDescribedBy) {
      contentDOM.setAttribute(ariaDescribedByAttribute, ariaDescribedBy);
    }

    if (ariaDetails) {
      contentDOM.setAttribute(ariaDetailsAttribute, ariaDetails);
    }

    if (ariaLabelledBy) {
      contentDOM.setAttribute(ariaLabelledByAttribute, ariaLabelledBy);
    } else {
      contentDOM.setAttribute(
        ariaLabelAttribute,
        ariaLabel ??
          intl.formatMessage({
            defaultMessage: 'Code Editor',
            id: 'EBcYgbL0S1T6EpS2',
            description: 'Aria-label for the code editors.',
          }),
      );
    }

    return () =>
      [
        ariaLabelAttribute,
        ariaLabelledByAttribute,
        ariaDetailsAttribute,
        ariaDescribedByAttribute,
      ].forEach((ariaAttribute) => contentDOM.removeAttribute(ariaAttribute));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ariaLabel, ariaLabelledBy, ariaDetails, ariaDescribedBy, intl]);

  // to match the css applied to the .cm-content based on size we need to remeasure the height
  // useLayoutEffect to match with the css change
  useLayoutEffect(() => {
    viewRef.current.requestMeasure();
  }, [viewRef, size]);

  const { messages, defaultLocale, locale } = useIntl();

  // Set state and extensions of the editor
  useEffect(() => {
    const editorView = viewRef.current;

    const handleValueChange = (transaction: Transaction) => {
      const newValue = transaction.newDoc.toString();

      if (valueRef.current !== newValue) {
        // Save the current transaction that updates the document to grab the most up-to-date selection range (cursor position).
        // This was done to fix cursor position resetting in APPDEV-9116.
        transactionRef.current = transaction;

        // Update the value ref
        valueRef.current = newValue;

        // Avoid calling the rest of logic including onChange if changes are coming from the value prop
        if (isMarkedDirtyRef.current) {
          isMarkedDirtyRef.current = false;
        } else {
          // Call the onChange from the editor props
          valueChangeRef.current?.(newValue);
        }
      } else {
        // Reset the marked dirty flag
        isMarkedDirtyRef.current = false;
      }
      return transaction;
    };

    editorView.setState(
      EditorState.create({
        // Initial value of the editor. `value` (controlled) takes precedence over
        // `defaultValue` (uncontrolled initial document). Both are mutually exclusive
        // at the type level, this guards against a runtime fallback.
        doc: value ?? defaultValue ?? '',
        extensions: [
          getDomEventHandlers(onFocus, onBlur),
          // Track IME composition state so controlled mode does not block candidate characters.
          EditorView.domEventObservers({
            compositionstart: () => {
              isUserEventComposeRef.current = true;
            },
            // Defer the reset by one microtask so the CodeMirror commit transaction
            // (dispatched synchronously during compositionend processing) still sees
            // isUserEventComposeRef as true and is not blocked in controlled mode.
            compositionend: () => {
              queueMicrotask(() => {
                isUserEventComposeRef.current = false;
              });
            },
          }),
          Prec.highest(keymapCompartment.of(getKeyMaps(viewRef))),
          languageCompartment.of([]),
          editorBaseConfigCompartment.of([]),
          // Indent unit is two spaces instead of tab
          indentUnit.of('  '),
          // Filter for transactions before they are applied. Controls which transactions
          // go through and allows editing/augmenting them.
          EditorState.transactionFilter.of((transaction) => {
            if (transaction.docChanged) {
              // Controlled-mode sync dispatches are annotated — let them through
              // and update internal state without calling onChange.
              if (transaction.annotation(controlledSyncAnnotation)) {
                return handleValueChange(transaction);
              }

              // In controlled mode, block user-initiated doc changes. Call onChange
              // so the consumer can update value, and save the transaction for cursor
              // position restoration when the new value is applied.
              // IME composition transactions are exempt so candidates display correctly.
              if (isControlledRef.current && !isUserEventComposeRef.current) {
                // Undo/redo must not be blocked — they need to mutate the editor
                // state so the undo stack is preserved, and onChange fires via
                // handleValueChange so the consumer can sync the new value.
                if (
                  transaction.isUserEvent('undo') ||
                  transaction.isUserEvent('redo')
                ) {
                  return handleValueChange(transaction);
                }
                // Autocomplete selections are user intent: apply the completion and
                // propagate via onChange so the consumer can accept/update value.
                if (transaction.isUserEvent('input.complete')) {
                  return handleValueChange(transaction);
                }
                const newValue = transaction.newDoc.toString();
                if (valueRef.current !== newValue) {
                  transactionRef.current = transaction;
                  valueChangeRef.current?.(newValue);
                  // The autocompletion extension only opens when it sees a
                  // docChanged user-input update. Since we block typing transactions,
                  // we signal the controlled sync microtask to call startCompletion
                  // after it updates the document, so completions are based on the new value.
                  pendingStartCompletionRef.current = true;
                }
                return [];
              }

              return handleValueChange(transaction);
            }
            return transaction;
          }),
          EditorView.updateListener.of((update) => {
            const isUndoOrRedo = update.transactions.some(
              (transaction) =>
                transaction.isUserEvent('undo') ||
                transaction.isUserEvent('redo'),
            );

            // Make sure that only folding was changed in the editor
            if (
              update.geometryChanged &&
              !update.docChanged &&
              !update.focusChanged &&
              update.heightChanged
            ) {
              const ranges = foldedRanges(update.state);
              const foldValues = calculateFolding(ranges);

              onFoldingChange?.(foldValues);
            }
            // Check if the transaction is undo or redo in order to not accidentally call onChange
            // twice. Usually onChange is called before applying the changes. However, undo and redo
            // are considered as transactions that occur after the view has been updated.
            // Therefore, we explicitly call onChange if that is the case.
            else if (update.docChanged && isUndoOrRedo) {
              update.transactions.forEach((transaction) =>
                handleValueChange(transaction),
              );
            }
          }),
          lineWrappingCompartment.of([]),
          placeholderCompartment.of([]),
          highlightOnChangeCompartment.of([]),
          formatKeymapCompartment.of([]),
        ],
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update base editor configuration (structure, layout, theme)
  useEffect(() => {
    const editorView = viewRef.current;
    const prev = prevEditorConfigRef.current;

    if (
      prev !== undefined &&
      prev.singleLine === singleLine &&
      prev.hideLineNumbers === hideLineNumbers &&
      prev.gutterConfiguration === gutterConfiguration &&
      prev.required === required &&
      prev.size === size &&
      prev.locale === locale &&
      prev.defaultLocale === defaultLocale &&
      prev.messages === messages
    ) {
      return;
    }

    prevEditorConfigRef.current = {
      singleLine,
      hideLineNumbers,
      gutterConfiguration,
      required,
      size,
      locale,
      defaultLocale,
      messages,
    };

    const intlProps: IntlProps = { locale, defaultLocale, messages };
    editorView.dispatch({
      effects: editorBaseConfigCompartment.reconfigure(
        getEditorConfiguration(intlProps, {
          singleLine,
          hideLineNumbers,
          gutterConfiguration,
          required,
          size,
        }),
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    singleLine,
    hideLineNumbers,
    gutterConfiguration,
    required,
    size,
    locale,
    defaultLocale,
    messages,
  ]);

  // Update language extensions (syntax, autocomplete)
  useEffect(() => {
    const editorView = viewRef.current;
    const prev = prevLangConfigRef.current;

    if (
      prev !== undefined &&
      prev.language === configuration?.language &&
      prev.extendWith === configuration?.extendWith &&
      prev.autocompleteOptions === configuration?.autocompleteOptions &&
      prev.locale === locale &&
      prev.defaultLocale === defaultLocale &&
      prev.messages === messages
    ) {
      return;
    }

    prevLangConfigRef.current = {
      language: configuration?.language,
      extendWith: configuration?.extendWith,
      autocompleteOptions: configuration?.autocompleteOptions,
      locale,
      defaultLocale,
      messages,
    };

    const intlProps: IntlProps = { locale, defaultLocale, messages };
    editorView.dispatch({
      effects: languageCompartment.reconfigure(
        getLanguageConfiguration(configuration || {}, intlProps),
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, locale, defaultLocale, messages]);

  useEffect(() => {
    configureReadOnly(readOnly, viewRef, keymapCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    configureLineWrap(lineWrap, viewRef, lineWrappingCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineWrap]);

  useEffect(() => {
    configurePlaceholder(placeholder, viewRef, placeholderCompartment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);

  useEffect(() => {
    configureSpellCheck(spellCheck, readOnly, viewRef);
  }, [viewRef, readOnly, spellCheck]);

  useEffect(() => {
    if (format) {
      configureFormatKeymap(handleFormat, viewRef, formatKeymapCompartment);
    } else {
      viewRef.current.dispatch({
        effects: formatKeymapCompartment.reconfigure([]),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format]);

  // Assign new onChange method to ref that is used in the updatelistener extension
  useEffect(() => {
    valueChangeRef.current =
      onChange !== undefined ? sanitizedOnChange : undefined;
  }, [onChange, sanitizedOnChange]);

  useEffect(() => {
    isControlledRef.current = isControlled;
  }, [isControlled]);

  // Set the content of the code editor to the changed value (non-controlled path).
  // When isControlled=true, value sync is driven by the during-render detection below.
  useEffect(() => {
    // In controlled mode value sync is handled via prevControlledValueRef during render.
    if (isControlledRef.current) {
      return;
    }
    // In uncontrolled mode (no `value` prop) the editor owns its document — prop-sync
    // is skipped so that `defaultValue` only seeds the initial document.
    if (value === undefined) {
      return;
    }
    const sanitizedValue = sanitizeValue(value);
    if (viewRef.current.state.doc.toString() === sanitizedValue) {
      return;
    }

    // Keep track of the previous value, mainly for highlighting purposes
    setPreviousValue(viewRef.current.state.doc.toString());

    // Set flag to true to indicate it was changed by someone other than user
    isMarkedDirtyRef.current = true;

    try {
      let selection;
      if (!viewRef.current.hasFocus) {
        // If the editor doesn't have focus, replace entire doc
        // with new value regardless of cursor position
        selection = undefined;
      } else {
        // Fallback to the view cursor position at last line
        const editorLineCount = viewRef.current.state.doc.lines;
        const lastLine = viewRef.current.state.doc.line(editorLineCount);
        // we use length -1 to avoid selection range error
        const selectionPosition = Math.max(0, lastLine.length - 1);

        // anchor and head define a text selection
        // if anchor and head have the same position the cursor is placed at a fixed position with no text selection
        selection = { anchor: selectionPosition, head: selectionPosition };
      }
      // Reset the transaction reference
      transactionRef.current = undefined;

      // Create a transaction to update the codeMirror document with the new value
      const transaction: Transaction = viewRef.current.state.update({
        changes: [
          {
            from: 0,
            to: viewRef.current.state.doc.length,
            insert: sanitizedValue,
          },
        ],
        selection,
      });

      viewRef.current.dispatch(transaction);
    } catch {
      /*
    Left empty on purpose. This was done to catch RangeErrors which would be thrown
    if the user is typing very fast. The selection cursor would be outside the CodeMirror's document length in some cases.
    Catching the error is better than crashing the entire code editor. */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Run the highlight extension while providing the previous value
  useEffect(() => {
    const editorView = viewRef.current;
    editorView.dispatch({
      effects: highlightOnChangeCompartment.reconfigure([
        highlightExtension(previousValue),
      ]),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousValue]);

  useEffect(() => {
    runFoldings(controlledFolding ?? [], viewRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledFolding]);

  // add aria-invalid if form validation returns an error
  useEffect(() => {
    const codeEditorInput = viewRef.current.dom.querySelector(
      'div[contenteditable]',
    );
    if (codeEditorInput) {
      hasError
        ? codeEditorInput.setAttribute('aria-invalid', 'true')
        : codeEditorInput.removeAttribute('aria-invalid');
      (codeEditorInput as HTMLElement).tabIndex = -1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  // Handle clicking outside the editor while having the autocomplete open to close it.
  const handleOutsideEditorClick = useCallback(
    (e: MouseEvent) => {
      const autoCompleteContainer = document.body.querySelector(
        '.cm-tooltip-autocomplete',
      );
      if (!autoCompleteContainer?.contains(e.target as Node)) {
        closeCompletion(viewRef.current);
      }
    },
    [viewRef],
  );

  useEffect(() => {
    // This way is preferred over handling it through the blur event on the editor as that closes the autocomplete
    // if the user attempts to scroll in the autocomplete tooltip
    // https://dev-jira.dynatrace.org/browse/ADES-4842
    document.addEventListener('pointerdown', handleOutsideEditorClick);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideEditorClick);
    };
  }, [handleOutsideEditorClick]);

  // https://dev-jira.dynatrace.org/browse/EXA-5783
  // forceParsing is a synchronous operation that blocks the main thread, but it keeps track
  // of the already parsed content. We parse in chunks, each blocking the main thread for 100ms
  // but pausing for 100ms after each parse attempt, this keeps the page responsive to scrolling and clicks
  // Note: This is really only a issue for very large json content like: `fetch spans | limit 5000` ( ~ 13 MB )
  useEffect(() => {
    if (!value) {
      return;
    }

    let timeoutId: number | null = null;
    let done = false;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };

    const tick = () => {
      const wholeSyntaxTreeParsed = syntaxTreeAvailable(
        viewRef.current.state,
        viewRef.current.state.doc.length,
      );

      if (wholeSyntaxTreeParsed) {
        cleanup();
        return;
      }

      done = forceParsing(
        viewRef.current,
        viewRef.current.state.doc.length,
        100,
      );

      if (!done) {
        timeoutId = window.setTimeout(tick, 10);
      }
    };

    tick();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // change the editor's focus behavior
  // only the .cm-scroll container is focusable via the keyboard, the content can be focused
  // by pressing 'ENTER' and only by pressing 'ESCAPE' the focus is moved back
  useEffect(() => {
    const editorScrollContainer =
      viewRef.current.dom.querySelector('.cm-scroller');
    editorScrollContainer?.setAttribute('tabIndex', '0');
    editorScrollContainer?.setAttribute('role', 'group');
    editorScrollContainer?.setAttribute(
      dataTestIdAttr,
      baseCodeEditorScrollerTestId,
    );

    const handleFocus = (event: Event) => {
      if (event instanceof FocusEvent) {
        focusProps.onFocus(event);
      }
    };
    const handleBlur = (event: Event) => {
      if (event instanceof FocusEvent) {
        focusProps.onBlur(event);
      }
    };
    const handleKeyDown = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        if (
          event.key === 'Enter' &&
          !event.metaKey &&
          !event.ctrlKey &&
          !readOnly
        ) {
          event.preventDefault();
          viewRef.current.focus();
        }
      }
    };
    editorScrollContainer?.addEventListener('focus', handleFocus);
    editorScrollContainer?.addEventListener('blur', handleBlur);
    editorScrollContainer?.addEventListener('keydown', handleKeyDown);

    return () => {
      editorScrollContainer?.removeEventListener('focus', handleFocus);
      editorScrollContainer?.removeEventListener('blur', handleBlur);
      editorScrollContainer?.removeEventListener('keydown', handleKeyDown);
    };
  }, [focusProps, readOnly, viewRef]);

  // Detect controlled value prop changes during render (not in useEffect) to avoid
  // the post-paint delay that causes a visible flash.
  //
  // Invariants: mutating prevControlledValueRef.current here is safe because this
  // code path only runs when isControlled=true and value is defined. React Strict
  // Mode's double-invocation is handled: the queueMicrotask captures a stable
  // valueToApply snapshot and guards with an equality check before dispatching, so
  // double-firing is a no-op. Concurrent-mode double-invocation: the first
  // invocation's microtask fires after the final commit and sees the live viewRef.
  if (isControlled && value !== undefined) {
    const sanitizedNew = sanitizeValue(value);
    if (prevControlledValueRef.current === undefined) {
      // First render — the editor was already seeded with this value in EditorState.create.
      prevControlledValueRef.current = sanitizedNew;
    }
    if (sanitizedNew !== prevControlledValueRef.current) {
      prevControlledValueRef.current = sanitizedNew;
      const valueToApply = sanitizedNew;
      queueMicrotask(() => {
        const shouldStartCompletion = pendingStartCompletionRef.current;
        pendingStartCompletionRef.current = false;
        if (viewRef.current.state.doc.toString() === valueToApply) {
          if (shouldStartCompletion) {
            startCompletion(viewRef.current);
          }
          return;
        }
        const lastTransaction = transactionRef.current;
        const savedCursorAnchor = lastTransaction?.newSelection?.main?.anchor;
        const anchor =
          savedCursorAnchor !== undefined
            ? Math.min(savedCursorAnchor, valueToApply.length)
            : undefined;
        // Update valueRef before dispatch so handleValueChange sees no change and
        // does not call onChange.
        valueRef.current = valueToApply;
        try {
          viewRef.current.dispatch(
            viewRef.current.state.update({
              changes: {
                from: 0,
                to: viewRef.current.state.doc.length,
                insert: valueToApply,
              },
              selection: anchor !== undefined ? { anchor } : undefined,
              annotations: controlledSyncAnnotation.of(true),
            }),
          );
        } catch {
          // Ignore RangeErrors from out-of-range cursor positions (can occur when
          // the parent re-renders with a shorter value while the user was typing).
        }
        if (shouldStartCompletion) {
          startCompletion(viewRef.current);
        }
      });
    }
  }

  return (
    <>
      <div
        ref={rootRef}
        className={clsx(
          styles.editorContainer({
            focusVisible: isFocusVisible,
            fullHeight,
            hasError,
            lineNumbers: singleLine || hideLineNumbers ? 'hidden' : undefined,
            readOnly,
            size,
          }),
          inputGroupClassName,
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...behavioralTrackingProps}
        {...restProps}
        data-dt-component={dataDtComponent}
      />
      <input
        type="text"
        id={id}
        disabled={readOnly}
        tabIndex={-1}
        onFocus={() =>
          // forward focus to the editor
          // we need the hidden input unfortunately since the editor does not support focus properly
          viewRef.current.focus()
        }
        aria-hidden="true"
        style={{
          position: 'absolute',
          height: 0,
          width: 0,
          opacity: 0,
          margin: 0,
          padding: 0,
          border: 0,
        }}
      />
      {(hasError || hint) && (
        <span
          aria-live="polite"
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
        >
          <BaseItem variant={hasError ? 'error' : 'hint'}>{hint}</BaseItem>
        </span>
      )}
    </>
  );
});

(
  BaseCodeEditor as typeof BaseCodeEditor & { displayName: string }
).displayName = 'BaseCodeEditor';
